import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { CodeBlock, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';
import tutorialStyles from './Tutorial.module.css';

export const Route = createFileRoute('/documentation/tutorial-vue')({
  beforeLoad: () => {
    throw redirect({ to: '/documentation/tutorial' });
  },
  component: TutorialVuePage,
});

function TutorialVuePage() {
  return (
    <div className={tutorialStyles.page}>
      <div className={styles.breadcrumb}>Getting Started</div>
      <h1 className={styles.pageTitle}>Stream in Vue</h1>
      <p className={styles.pageSubtitle}>
        Add a GPU-rendered stream to a Vue app in about five minutes.
      </p>

      <div className={tutorialStyles.badge}>
        <span>~5 min</span>
        <span className={tutorialStyles.badgeSep} />
        <span>3 steps</span>
      </div>

      {/* ── Video placeholder ── */}
      <div className={tutorialStyles.videoPlaceholder}>
        <div className={tutorialStyles.videoPlayIcon}>
          <div className={tutorialStyles.videoPlayTriangle} />
        </div>
        <span className={tutorialStyles.videoLabel}>
          Watch the framework walkthrough (4:30)
        </span>
      </div>

      {/* ── Prerequisites ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="prerequisites">
          Before You Begin
        </h2>
        <ul className={tutorialStyles.prereqList}>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              <span className={styles.emphasis}>Node.js 18+</span> and npm
              (or pnpm / yarn)
            </span>
          </li>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              An <span className={styles.emphasis}>admission token</span> — see
              the{' '}
              <Link to="/documentation/tutorial" hash="prerequisites">
                HTML tutorial prerequisites
              </Link>
            </span>
          </li>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              Familiarity with Vue 3 Composition API and Single File Components
            </span>
          </li>
        </ul>
        <InfoCallout variant="tip">
          New to pixel-stream? Start with the{' '}
          <Link to="/documentation/tutorial">HTML tutorial</Link> first — it
          covers the core concepts without any framework overhead.
        </InfoCallout>
      </section>

      {/* ── Step 1 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="scaffold-the-project">
          Step 1 — Scaffold the Project
        </h2>
        <p className={styles.prose}>
          Create a new Vite + Vue + TypeScript project and install the
          pixel-stream package:
        </p>
        <CodeBlock language="bash" title="Terminal">
{`npm create vite@latest my-stream -- --template vue-ts
cd my-stream
npm install @interlucent/pixel-stream`}
        </CodeBlock>
      </section>

      {/* ── Step 2 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="add-the-stream">
          Step 2 — Add the Stream Component
        </h2>
        <p className={styles.prose}>
          Vue needs to know that <code>&lt;pixel-stream&gt;</code> is a native
          custom element, not a Vue component. Add one line to your Vite config:
        </p>
        <CodeBlock language="typescript" title="vite.config.ts">
{`import { defineConfig } from 'vite';
import vue from '@vitejs/plugin-vue';

export default defineConfig({
  plugins: [
    vue({
      template: {
        compilerOptions: {
          isCustomElement: (tag) => tag === 'pixel-stream',
        },
      },
    }),
  ],
});`}
        </CodeBlock>
        <InfoCallout variant="warning">
          Without the <code>isCustomElement</code> config, Vue emits a runtime
          warning and the element may not render correctly.
        </InfoCallout>
        <p className={styles.prose}>
          Now replace <code>src/App.vue</code> with the streaming component:
        </p>
        <CodeBlock language="html" title="src/App.vue">
{`<script setup lang="ts">
import { ref, onMounted } from 'vue';
import '@interlucent/pixel-stream';

const psRef = ref<HTMLElement | null>(null);

onMounted(() => {
  if (psRef.value) {
    (psRef.value as any).admissionToken = 'YOUR_TOKEN';
  }
});
</script>

<template>
  <pixel-stream
    ref="psRef"
    controls
    style="display: block; width: 100vw; height: 100vh"
  />
</template>`}
        </CodeBlock>
        <p className={styles.prose}>
          The <code>import</code> registers the custom element. Vue's template
          ref gives you the raw DOM element, so property access and method
          calls work exactly like vanilla JavaScript.
        </p>
      </section>

      {/* ── Step 3 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="run-and-play">
          Step 3 — Run and Play
        </h2>
        <CodeBlock language="bash" title="Terminal">
{`npm run dev`}
        </CodeBlock>
        <p className={styles.prose}>
          Open the URL shown in your terminal. You will see a dark viewport with
          a <span className={styles.emphasis}>play button</span>. Click it —
          the component connects to the platform, requests a GPU worker, and
          starts streaming. Move your mouse to interact. That's it — you're
          streaming from Vue.
        </p>
        <InfoCallout variant="warning">
          If nothing appears, open the browser console (F12). The most common
          issue is an expired admission token — generate a fresh one and
          replace the value in <code>App.vue</code>.
        </InfoCallout>
      </section>

      {/* ── Make It Yours ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="make-it-yours">
          Make It Yours
        </h2>

        <h3 className={styles.subsectionTitle} id="custom-overlay">
          Custom Overlay
        </h3>
        <p className={styles.prose}>
          Replace the built-in controls with your own Vue template using the{' '}
          <code>overlay</code> slot. The <code>&lt;pixel-stream&gt;</code>{' '}
          element sets a <code>status</code> attribute on itself as the session
          progresses — <code>idle</code>, <code>connecting</code>,{' '}
          <code>queued</code>, <code>streaming</code>, <code>failed</code>,
          etc. Use CSS attribute selectors to show the right state:
        </p>
        <CodeBlock language="html" title="StreamOverlay.vue">
{`<script setup lang="ts">
const props = defineProps<{ psRef: HTMLElement | null }>();
const ps = () => props.psRef as any;
</script>

<template>
  <div slot="overlay" class="overlay">
    <div class="state state-idle">
      <button @click="ps()?.play()">Launch Experience</button>
    </div>
    <div class="state state-pending">
      <div class="spinner" />
      <p>Preparing your session\u2026</p>
      <button @click="ps()?.cancel()">Cancel</button>
    </div>
    <div class="state state-error">
      <p>Something went wrong.</p>
      <button @click="ps()?.play()">Try Again</button>
    </div>
  </div>
</template>

<style>
.state { display: none; }

pixel-stream[status="idle"]           .state-idle,
pixel-stream[status="connected"]      .state-idle    { display: flex; }

pixel-stream[status="connecting"]     .state-pending,
pixel-stream[status="authenticating"] .state-pending,
pixel-stream[status="queued"]         .state-pending,
pixel-stream[status="negotiating"]    .state-pending { display: flex; }

pixel-stream[status="failed"]        .state-error,
pixel-stream[status="ended"]         .state-error   { display: flex; }

pixel-stream[status="streaming"]     .overlay { display: none; }
</style>`}
        </CodeBlock>
        <InfoCallout variant="info">
          The CSS must be unscoped (no <code>scoped</code> attribute on the{' '}
          <code>&lt;style&gt;</code> tag) because the{' '}
          <code>pixel-stream[status]</code> selector targets an element outside
          the component's scope. Alternatively, use{' '}
          <code>:deep()</code> within a scoped block. See the{' '}
          <Link to="/documentation/streaming-guide" hash="custom-overlay">
            Streaming Guide
          </Link>{' '}
          for the full overlay pattern.
        </InfoCallout>

        <h3 className={styles.subsectionTitle} id="listen-for-events">
          Listen for Events
        </h3>
        <p className={styles.prose}>
          Use <code>addEventListener</code> on the template ref in{' '}
          <code>onMounted</code>:
        </p>
        <CodeBlock language="typescript" title="events via onMounted">
{`onMounted(() => {
  psRef.value?.addEventListener('status-change', (e: Event) => {
    const { newStatus } = (e as CustomEvent).detail;
    console.log('Status:', newStatus);
  });

  psRef.value?.addEventListener('data-channel-open', () => {
    console.log('Data channel ready');
  });
});`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="send-a-message">
          Send a Message
        </h3>
        <CodeBlock language="typescript" title="messaging via ref">
{`onMounted(() => {
  psRef.value?.addEventListener('data-channel-open', () => {
    (psRef.value as any).sendUIInteraction({
      action: 'setColor',
      target: 'car-body',
      color: '#FF5500',
    });
  });
});`}
        </CodeBlock>
        <InfoCallout variant="warning">
          Always wait for the <code>data-channel-open</code> event before
          calling <code>sendUIInteraction()</code> or{' '}
          <code>sendCommand()</code>.
        </InfoCallout>
      </section>

      {/* ── Next Steps ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="next-steps">
          Next Steps
        </h2>
        <div className={styles.linkGrid}>
          <Link
            to="/documentation/streaming-guide"
            className={styles.linkCard}
          >
            <div className={styles.linkCardTitle}>Streaming Guide</div>
            <div className={styles.linkCardDesc}>
              Configuration, custom overlays with CSS status selectors,
              bidirectional messaging, and a full configurator example.
            </div>
          </Link>
          <Link to="/documentation/tutorial" className={styles.linkCard}>
            <div className={styles.linkCardTitle}>HTML Tutorial</div>
            <div className={styles.linkCardDesc}>
              The framework-free version — CDN script tag, no build step.
              Covers the same concepts without Vue overhead.
            </div>
          </Link>
          <Link
            to="/documentation/sdk-reference"
            className={styles.linkCard}
          >
            <div className={styles.linkCardTitle}>SDK Reference</div>
            <div className={styles.linkCardDesc}>
              Generate admission tokens server-side. Minimal, production,
              low-latency, and resilient token patterns.
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
